import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { isEmbeddedImage } from '../utils/imageEmbed';
import { isSvgMarkup, makeSvgResponsive } from '../utils/fabricSvg';
import SafeHtml from './SafeHtml';
import { prepareCustomHtmlBlock } from '../utils/customCss';
import { LAYOUT_WIDTH, LAYOUT_HEIGHT } from '../utils/customHtmlRaster';

/**
 * SlideThumbnailPreview
 * ---------------------
 * Renders a small, faithful miniature of a slide for the outline drawer.
 *
 * Instead of a single generic placeholder, this reads the slide's actual
 * blocks and sketches each one: real heading/paragraph text, real images,
 * and lightweight structural representations for cards, timelines, tables,
 * code, etc. The result reads like a scaled-down version of the slide while
 * staying cheap to render (no live editors, iframes, or media playback).
 *
 * The whole tree is built from <span>/<img> elements so it remains valid
 * markup when nested inside the drawer's <button> slide targets.
 */

const MAX_BLOCKS = 5;

const toText = (html = '') =>
  String(html || '')
    .replace(/<br\s*\/?>(?=)/gi, ' ')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&#39;/gi, "'")
    .replace(/&quot;/gi, '"')
    .replace(/\s+/g, ' ')
    .trim();

const headingLevel = (html = '') => {
  const match = /<h([1-6])/i.exec(html);
  return match ? Number(match[1]) : 2;
};

const listItems = (html = '', limit = 4) => {
  const items = [];
  const regex = /<li[^>]*>([\s\S]*?)<\/li>/gi;
  let match;
  while ((match = regex.exec(html)) !== null && items.length < limit) {
    const text = toText(match[1]);
    if (text) items.push(text);
  }
  if (items.length === 0) {
    const text = toText(html);
    if (text) items.push(text);
  }
  return items;
};

const BOX_STYLES = {
  'info-box': { border: '#60a5fa', bg: 'rgba(96, 165, 250, 0.14)' },
  'exercise-box': { border: '#34d399', bg: 'rgba(52, 211, 153, 0.14)' },
  'warning-box': { border: '#fbbf24', bg: 'rgba(251, 191, 36, 0.16)' }
};

const firstText = (...values) => {
  for (const value of values) {
    const text = toText(value);
    if (text) return text;
  }
  return '';
};

const GenericLines = ({ count = 2 }) => (
  <span className="stp-lines">
    {Array.from({ length: count }).map((_, index) => (
      <span
        key={index}
        className="stp-lines__row"
        style={{ width: index === count - 1 ? '55%' : '85%' }}
      />
    ))}
  </span>
);

/**
 * A miniature of a Custom HTML/CSS block.
 *
 * The layout is laid out at the real slide size (LAYOUT_WIDTH x LAYOUT_HEIGHT)
 * and then scaled down with a transform, rather than being reflowed into a
 * 150px-wide box. That matters because these layouts are hand-written: one
 * sized in `cqw` would reflow gracefully, but one sized in px, or one using a
 * grid with fixed tracks, would collapse into an unreadable smear. Laying out
 * at slide size and scaling means every layout miniaturises the same way the
 * PowerPoint/PDF rasteriser does — the thumbnail is the slide, just smaller.
 */
const CustomHtmlMini = ({ block }) => {
  const { html, css, imageVarsCss, baseCss } = useMemo(
    () => prepareCustomHtmlBlock(block),
    [block]
  );
  // No reset: the drawer is on the app page, which already runs preflight.
  const sheet = [baseCss, imageVarsCss, css].filter(Boolean).join('\n');
  const hostRef = useRef(null);
  const [scale, setScale] = useState(null);

  useLayoutEffect(() => {
    const host = hostRef.current;
    if (!host) return undefined;
    // Fit, not fill: on a slide the layout shares with other blocks the host
    // is shorter than the content region's proportions, and scaling on width
    // alone would push the bottom of the layout out of the thumbnail.
    const measure = () => {
      const { clientWidth: width, clientHeight: height } = host;
      if (width > 0 && height > 0) {
        setScale(Math.min(width / LAYOUT_WIDTH, height / LAYOUT_HEIGHT));
      }
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(host);
    return () => observer.disconnect();
  }, []);

  return (
    <span
      className="stp-custom-html"
      ref={hostRef}
      // The box matches the slide's content region, which is wider than 16:9 —
      // the slide header is not part of what this block renders.
      style={{ aspectRatio: `${LAYOUT_WIDTH} / ${LAYOUT_HEIGHT}` }}
    >
      {sheet ? <style>{sheet}</style> : null}
      <SafeHtml
        as="span"
        profile="slideLayout"
        className="slide-custom-html stp-custom-html__stage"
        data-block={block.id}
        // Until the host has been measured the stage stays hidden, so a
        // full-size layout never flashes across the drawer on first paint.
        style={{
          width: `${LAYOUT_WIDTH}px`,
          height: `${LAYOUT_HEIGHT}px`,
          transform: `scale(${scale == null ? 0 : scale})`,
        }}
        html={html}
      />
    </span>
  );
};

const renderMiniBlock = (block) => {
  if (!block || block.type === 'divider') return null;

  switch (block.type) {
    case 'heading': {
      const text = toText(block.content);
      if (!text) return null;
      return <span className={`stp-heading stp-heading--h${headingLevel(block.content)}`}>{text}</span>;
    }

    case 'headline': {
      const text = toText(block.content || block.title);
      if (!text) return null;
      return <span className="stp-heading stp-heading--h1">{text}</span>;
    }

    case 'text':
    case 'html': {
      // A shape or drawing from the builder is stored as an html block holding
      // fabric SVG. Scraping it for text yields its <desc> ("Created with
      // Fabric.js"), which is how the outline ended up showing that line
      // instead of the artwork. Draw the real thing instead.
      if (isSvgMarkup(block.content)) {
        return (
          <SafeHtml
            as="span"
            className="stp-shape"
            html={makeSvgResponsive(block.content)}
          />
        );
      }
      const text = toText(block.content);
      if (!text) return null;
      return <span className="stp-text">{text}</span>;
    }

    case 'list': {
      const items = listItems(block.content, 4);
      if (items.length === 0) return null;
      return (
        <span className="stp-list">
          {items.slice(0, 4).map((text, index) => (
            <span key={index} className="stp-list__item">
              <span className="stp-list__dot" />
              <span className="stp-list__text">{text}</span>
            </span>
          ))}
        </span>
      );
    }

    case 'info-box':
    case 'exercise-box':
    case 'warning-box': {
      const style = BOX_STYLES[block.type];
      const text = toText(block.content);
      return (
        <span className="stp-box" style={{ borderLeftColor: style.border, background: style.bg }}>
          {text ? <span className="stp-box__text">{text}</span> : <GenericLines count={2} />}
        </span>
      );
    }

    case 'apa-quote': {
      const text = firstText(block.content, block.title);
      return (
        <span className="stp-quote">
          <span className="stp-quote__mark">&ldquo;</span>
          <span className="stp-quote__text">{text || <GenericLines count={2} />}</span>
        </span>
      );
    }

    case 'apa-citation':
    case 'apa-reference-list': {
      return (
        <span className="stp-refs">
          {Array.from({ length: 3 }).map((_, index) => (
            <span key={index} className="stp-refs__row" style={{ width: `${90 - index * 12}%` }} />
          ))}
        </span>
      );
    }

    case 'image_drawover': {
      // Draw-overs keep their artwork in `content` — a PNG data URL when there
      // was a backdrop photo, otherwise fabric SVG. The old code looked at
      // `block.src`, which these blocks never set, so every draw-over showed
      // an empty thumbnail.
      const artwork = block.content;
      if (typeof artwork === 'string' && artwork.startsWith('data:image/')) {
        return (
          <span className="stp-media">
            <img className="stp-media__img" src={artwork} alt="" loading="lazy" decoding="async" />
          </span>
        );
      }
      if (isSvgMarkup(artwork)) {
        return (
          <SafeHtml as="span" className="stp-shape" html={makeSvgResponsive(artwork)} />
        );
      }
      if (!block.src) return null;
      return (
        <span className="stp-media">
          <img className="stp-media__img" src={block.src} alt="" loading="lazy" decoding="async" />
        </span>
      );
    }

    case 'image': {
      // A provider-hosted embed cannot be rendered as a live frame this small,
      // but the preview captured for the slide exports doubles as its
      // thumbnail. Without one, fall back to a labelled frame.
      if (isEmbeddedImage(block)) {
        if (block.imageEmbedThumbnail) {
          return (
            <span className="stp-media">
              <img className="stp-media__img" src={block.imageEmbedThumbnail} alt="" loading="lazy" decoding="async" />
            </span>
          );
        }
        return (
          <span className="stp-embed">
            <span className="stp-embed__label">{block.imageEmbedProvider || 'Embed'}</span>
          </span>
        );
      }
      if (!block.src) return null;
      return (
        <span className="stp-media">
          <img className="stp-media__img" src={block.src} alt="" loading="lazy" decoding="async" />
        </span>
      );
    }

    case 'gallery': {
      const items = (block.items || []).filter((item) => item?.src).slice(0, 4);
      if (items.length === 0) return null;
      return (
        <span className={`stp-gallery stp-gallery--${Math.min(items.length, 4)}`}>
          {items.map((item, index) => (
            <img key={index} className="stp-gallery__img" src={item.src} alt="" loading="lazy" decoding="async" />
          ))}
        </span>
      );
    }

    case 'video': {
      const poster = block.poster || block.thumbnail || block.posterSrc;
      return (
        <span className="stp-video">
          {poster ? <img className="stp-media__img" src={poster} alt="" loading="lazy" decoding="async" /> : null}
          <span className="stp-video__play" />
        </span>
      );
    }

    // A playlist reads as a player with its running order beside it — the same
    // shape the block draws on the slide, at thumbnail size.
    case 'youtube-playlist': {
      const entries = (block.items || []).slice(0, 3);
      const rows = entries.length ? entries : [null, null, null];
      return (
        <span className="stp-playlist">
          <span className="stp-playlist__player">
            <span className="stp-video__play" />
          </span>
          <span className="stp-playlist__rows">
            {rows.map((entry, index) => (
              <span key={index} className="stp-playlist__row">
                <span className="stp-playlist__thumb" />
                <span className="stp-playlist__label">
                  {entry ? toText(entry.title) || `Video ${index + 1}` : ''}
                </span>
              </span>
            ))}
          </span>
        </span>
      );
    }

    case 'audio': {
      return (
        <span className="stp-audio">
          <span className="stp-audio__dot" />
          <span className="stp-audio__wave">
            {[9, 14, 7, 16, 10, 13, 6].map((height, index) => (
              <span key={index} className="stp-audio__bar" style={{ height: `${height}px` }} />
            ))}
          </span>
        </span>
      );
    }

    case 'cards': {
      const items = (block.items || [])
        .filter((item) => toText(item?.title) || toText(item?.content))
        .slice(0, 3);
      if (items.length === 0) return null;
      return (
        <span className="stp-cards">
          {items.map((item, index) => (
            <span key={index} className="stp-card">
              <span className="stp-card__title">{firstText(item.title, item.content)}</span>
              <span className="stp-card__line" />
            </span>
          ))}
        </span>
      );
    }

    case 'stacked-cards': {
      const items = (block.items || [])
        .filter((item) => toText(item?.title) || toText(item?.content) || toText(item?.sideText))
        .slice(0, 3);
      if (items.length === 0) return null;
      return (
        <span className="stp-stack">
          {items.map((item, index) => (
            <span key={index} className="stp-stack__row">
              <span className="stp-stack__badge" />
              <span className="stp-stack__title">{firstText(item.title, item.content, item.sideText)}</span>
            </span>
          ))}
        </span>
      );
    }

    case 'multi-column-cards': {
      const columns = (block.columns || []).slice(0, 3);
      if (columns.length === 0) return null;
      return (
        <span className="stp-columns">
          {columns.map((column, colIndex) => {
            const cards = (column.cards || []).slice(0, 2);
            return (
              <span key={colIndex} className="stp-columns__col">
                {(cards.length ? cards : [null]).map((card, cardIndex) => (
                  <span key={cardIndex} className="stp-columns__card">
                    <span className="stp-columns__title">{card ? firstText(card.title, card.content) : ''}</span>
                  </span>
                ))}
              </span>
            );
          })}
        </span>
      );
    }

    case 'project-timeline': {
      const steps = (block.steps || []).filter(
        (step) => toText(step?.title) || toText(step?.text) || toText(step?.timeSpan)
      );
      const title = toText(block.title);
      const dotCount = Math.min(Math.max(steps.length, 3), 6);
      return (
        <span className="stp-timeline">
          {title ? <span className="stp-timeline__title">{title}</span> : null}
          <span className="stp-timeline__track">
            <span className="stp-timeline__line" />
            {Array.from({ length: dotCount }).map((_, index) => (
              <span key={index} className="stp-timeline__node" />
            ))}
          </span>
        </span>
      );
    }

    case 'worksheet': {
      const questions = (block.questions || []).slice(0, 3);
      if (questions.length === 0) return <GenericLines count={2} />;
      return (
        <span className="stp-worksheet">
          {questions.map((question, index) => (
            <span key={index} className="stp-worksheet__row">
              <span className="stp-worksheet__num">{index + 1}</span>
              <span className="stp-worksheet__line" />
            </span>
          ))}
        </span>
      );
    }

    case 'link': {
      const items = (block.items || [])
        .filter((item) => toText(item?.title) || item?.url)
        .slice(0, 3);
      if (items.length === 0) return null;
      return (
        <span className="stp-links">
          {items.map((item, index) => (
            <span key={index} className="stp-links__row">
              <span className="stp-links__glyph" />
              <span className="stp-links__text">{firstText(item.title, item.url)}</span>
            </span>
          ))}
        </span>
      );
    }

    case 'table': {
      return (
        <span className="stp-table">
          {Array.from({ length: 3 }).map((_, rowIndex) => (
            <span key={rowIndex} className={`stp-table__row ${rowIndex === 0 ? 'stp-table__row--head' : ''}`}>
              {Array.from({ length: 3 }).map((__, colIndex) => (
                <span key={colIndex} className="stp-table__cell" />
              ))}
            </span>
          ))}
        </span>
      );
    }

    case 'accordion': {
      return (
        <span className="stp-accordion">
          {Array.from({ length: 3 }).map((_, index) => (
            <span key={index} className="stp-accordion__row" />
          ))}
        </span>
      );
    }

    // A hand-written layout is the one block type whose whole point is how it
    // looks, so the thumbnail shows the real thing rather than a stack of grey
    // bars.
    case 'custom-html': {
      const { html } = prepareCustomHtmlBlock(block);
      if (!html.trim()) return null;
      return <CustomHtmlMini block={block} />;
    }

    case 'embedded-html': {
      return (
        <span className="stp-code">
          {['62%', '80%', '46%', '70%'].map((width, index) => (
            <span key={index} className="stp-code__row" style={{ width }} />
          ))}
        </span>
      );
    }

    default: {
      const text = firstText(block.content, block.title);
      if (text) return <span className="stp-text">{text}</span>;
      return <GenericLines count={2} />;
    }
  }
};

const MEDIA_TYPES = new Set(['image', 'image_drawover', 'video']);

const SlideThumbnailPreview = ({ blocks = [], headerColor = '#545f7d', bodyStyle, className = '' }) => {
  const nodes = useMemo(() => {
    const meaningful = [];
    blocks.forEach((block, index) => {
      const node = renderMiniBlock(block);
      if (node) meaningful.push({ node, key: block?.id || `block-${index}`, type: block?.type });
    });

    // A hand-written layout takes over the body of its slide, so it has to
    // survive the MAX_BLOCKS cut. Adding one to a slide that already had five
    // blocks otherwise pushed it into the "+1" counter — the thumbnail went on
    // showing the old content and the new layout was nowhere in the drawer.
    const layouts = new Set(
      meaningful.filter(({ type }) => type === 'custom-html').map(({ key }) => key)
    );
    const budget = Math.max(MAX_BLOCKS - layouts.size, 0);
    let spent = 0;
    const rendered = meaningful.filter(({ key }) => {
      if (layouts.has(key)) return true;
      if (spent >= budget) return false;
      spent += 1;
      return true;
    });

    return { rendered, hiddenCount: meaningful.length - rendered.length };
  }, [blocks]);

  // A lone image/video fills the slide body; when it shares the slide with
  // other blocks it is capped to a row so the surrounding text stays visible.
  const soleBlock = nodes.rendered.length === 1;
  // A hand-written layout is the slide body, not a paragraph inside it, so when
  // it is the only thing on the slide the thumbnail gives it the full box.
  const soleCustomHtml = soleBlock && nodes.rendered[0].type === 'custom-html';

  return (
    <span className={`stp drawer-slide-thumb--large ${className}`.trim()} aria-hidden="true">
      <span className="stp__bar" style={{ backgroundColor: headerColor }} />
      <span
        className={`stp__body${soleCustomHtml ? ' stp__body--custom-html' : ''}`}
        style={bodyStyle}
      >
        {nodes.rendered.length === 0 ? (
          <span className="stp__empty">Empty slide</span>
        ) : (
          nodes.rendered.map(({ node, key, type }) => {
            const isMedia = MEDIA_TYPES.has(type);
            const blockClass = [
              'stp__block',
              isMedia ? 'stp__block--media' : '',
              isMedia && soleBlock ? 'stp__block--fill' : '',
              type === 'custom-html' && soleCustomHtml ? 'stp__block--custom-html' : ''
            ]
              .filter(Boolean)
              .join(' ');
            return (
              <span key={key} className={blockClass}>
                {node}
              </span>
            );
          })
        )}
        {nodes.hiddenCount > 0 ? <span className="stp__more">+{nodes.hiddenCount}</span> : null}
      </span>
    </span>
  );
};

export default SlideThumbnailPreview;
